import { router as inertiaRouter, usePoll } from '@inertiajs/react';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Mail from 'lucide-react/dist/esm/icons/mail';
import type React from 'react';
import { type FormEventHandler, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { FormPasswordInput } from '@/components/ui/form/FormPasswordInput';
import { FormTextInput } from '@/components/ui/form/FormTextInput';
import { cn } from '@/lib/utils';

import { type FieldErrors, firstErrorPerField } from './authFormErrors';
import { AuthStatusMessage } from './AuthStatusMessage';
import { type AuthFormRouter, ForgotPasswordForm } from './ForgotPasswordForm';
import { inlineTwoFactorFields } from './inlineTwoFactorFields';

/**
 * `verify-passwordless` is the verify step for an account with no password to
 * prompt for. It is a step of its own rather than a flag beside `verify` so the
 * surrounding surface can describe it — the hosted page's card subtitle would
 * otherwise still be telling the visitor to enter a password.
 */
export type DynamicLoginStep = 'email' | 'verify' | 'verify-passwordless' | 'forgot';

const MAGIC_LINK_SENT_MESSAGE =
  'If an account exists for that email address, a sign-in link is on its way.';

// User-facing auth prompt string, not a credential or secret.
// react-doctor-disable-next-line react-doctor/no-secrets-in-client-code
const SET_PASSWORD_PROMPT = "This account doesn't have a password yet.";

const MAGIC_LINK_PROMPT =
  "Prefer a Magic Link? We'll email you a link that signs you in instantly — no password needed.";

const SET_PASSWORD_SENT_MESSAGE =
  'If an account exists for that email address, a link to set your password is on its way.';

const NO_SIGN_IN_METHOD_MESSAGE =
  "This account can't sign in yet. Please contact an administrator to finish setting it up.";

const SESSION_POLL_INTERVAL_MS = 3_000;

/**
 * Matches the sign-in link's own lifetime (`auth.magic_link.expire`, 15 minutes by
 * default): once the link can no longer be used there is nothing left to wait for.
 */
const SESSION_POLL_TIMEOUT_MS = 15 * 60 * 1_000;

const DEFAULT_SESSION_PROPS = ['identity'];

/**
 * Which verify step the identified record earns. The identify endpoint flashes
 * whether the record has a password, so the answer arrives as shared state on the
 * page its redirect renders — the same answer on the hosted login page and on a
 * workspace page. Assume it has one whenever the round-trip cannot say, so an
 * unanswered identify step still offers the ordinary password prompt.
 */
function identifiedVerifyStep(page: unknown): DynamicLoginStep {
  const flag = (page as { props?: { session?: { identifiedHasPassword?: unknown } } } | undefined)
    ?.props?.session?.identifiedHasPassword;

  return flag === false ? 'verify-passwordless' : 'verify';
}

export type DynamicLoginFormProps = {
  /** The authenticatable resource to sign in against. */
  resourceId: string;

  /** Whether the resource has a password field, and so can offer a reset link. */
  passwordResetEnabled?: boolean;

  /** Whether the resource opted in to emailed sign-in links. */
  magicLinkEnabled?: boolean;

  /**
   * Id of the page to land on after a successful sign-in — a stable page id, never a
   * path; the server resolves the page's route itself. Carried through the emailed
   * sign-in link as well, so both routes into the session land in the same place.
   */
  redirectPage?: string;

  /** A server-flashed message to surface above the form, e.g. after a password reset. */
  statusMessage?: string | null;

  /**
   * The Inertia props the host surface reads the signed-in identity from. While an
   * emailed sign-in link is pending they are reloaded every few seconds, so the tab
   * that requested the link signs in on its own once the link is used elsewhere in the
   * same browser. Pass a stable array; defaults to the shared `identity` prop.
   */
  sessionProps?: string[];

  /** The served Page id used to bind an emailed link to the referring page URL. */
  currentPageId?: string | null;

  identifierLabel?: React.ReactNode;
  identifierPlaceholder?: string;
  passwordLabel?: React.ReactNode;
  passwordPlaceholder?: string;
  continueLabel?: React.ReactNode;
  submitLabel?: React.ReactNode;
  magicLinkLabel?: React.ReactNode;

  /** Contents of the button an account with no password yet uses to set one. */
  setPasswordLabel?: React.ReactNode;

  className?: string;
  buttonClassName?: string;

  /**
   * Whether this form is embedded in a workspace page runtime (`app-page` shell).
   * When true, a successful same-origin password sign-in is answered with a 302
   * the Inertia router follows in-page; when false or omitted the server emits a
   * full-page `X-Inertia-Location` redirect.
   */
  embedded?: boolean;

  /**
   * Return the visitor to the page this form was submitted from after password or
   * Magic Link sign-in, path parameters and query string included. Takes precedence
   * over `redirectPage`; the server resolves the destination itself.
   */
  returnTo?: 'current';

  /**
   * Stay on this page when the sign-in needs two-factor. The host surface then renders
   * the challenge or the setup for the pending sign-in. Needs `embedded`.
   */
  twoFactor?: 'inline';

  /** Notifies the host surface which step is showing, for surrounding copy and controls. */
  onStepChange?: (step: DynamicLoginStep) => void;

  /** Sends every request. Defaults to Inertia's router; the page playground passes one that blocks them. */
  router?: AuthFormRouter;
};

/**
 * The Dynamic Auth sign-in flow, shared by the Diagonal-hosted login page and the
 * page runtime's `Sdk.Components.LoginForm`, so both offer the same options against
 * the same endpoints.
 *
 * Collects the email first and confirms it resolves to a record before revealing how
 * to verify it, which is what lets the second step offer a password, an emailed
 * sign-in link, or both, depending on what the resource enabled. Every request goes
 * through Inertia's router, so CSRF, session regeneration, and rate limiting stay
 * server-side.
 */
export function DynamicLoginForm({
  resourceId,
  passwordResetEnabled = false,
  magicLinkEnabled = false,
  redirectPage,
  statusMessage = null,
  sessionProps = DEFAULT_SESSION_PROPS,
  currentPageId = null,
  identifierLabel = 'Email',
  identifierPlaceholder = 'Email',
  passwordLabel = 'Password',
  passwordPlaceholder = 'Password',
  continueLabel = 'Continue',
  submitLabel = 'Continue with Password',
  magicLinkLabel = 'Email me a sign-in link',
  setPasswordLabel = 'Email me a link to set my password',
  className,
  buttonClassName,
  embedded,
  returnTo,
  twoFactor,
  onStepChange,
  router = inertiaRouter
}: DynamicLoginFormProps) {
  const [step, setStep] = useState<DynamicLoginStep>('email');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [identifying, setIdentifying] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [magicLinkProcessing, setMagicLinkProcessing] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [statusConsumed, setStatusConsumed] = useState(false);
  const [setPasswordProcessing, setSetPasswordProcessing] = useState(false);
  const [setPasswordSent, setSetPasswordSent] = useState(false);

  // The emailed link opens in another tab, which is where the session cookie gets
  // set — this tab already shares it and only has to notice. Polling the identity
  // props does that without any server-side state: on a workspace page the runtime
  // re-renders as signed in, and the hosted login page is redirected to the login
  // Page by its guest middleware the moment a reload finds a session.
  const sessionPoll = usePoll(
    SESSION_POLL_INTERVAL_MS,
    { only: sessionProps },
    { autoStart: false }
  );

  useEffect(() => {
    if (!magicLinkSent) {
      return;
    }

    const checkSession = () => {
      if (document.visibilityState === 'visible') {
        router.reload({ only: sessionProps });
      }
    };

    sessionPoll.start();
    document.addEventListener('visibilitychange', checkSession);
    const timeout = window.setTimeout(sessionPoll.stop, SESSION_POLL_TIMEOUT_MS);

    return () => {
      sessionPoll.stop();
      document.removeEventListener('visibilitychange', checkSession);
      window.clearTimeout(timeout);
    };
  }, [magicLinkSent, router, sessionPoll, sessionProps]);

  /**
   * Every banner and inline error describes what just happened on the step the visitor
   * was on, so moving between steps clears them all. `statusMessage` needs its own flag
   * because it is a prop: the reset step posts to a route that redirects back, which
   * refreshes the host page's flashed status even though the message belongs to a form
   * the visitor has since left.
   */
  const goToStep = (next: DynamicLoginStep) => {
    setErrors({});
    setMagicLinkSent(false);
    setSetPasswordSent(false);
    setStatusConsumed(true);
    setStep(next);
    onStepChange?.(next);
  };

  const submitIdentifier: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setErrors({});

    router.post(
      `/auth/resource/${resourceId}/identify`,
      { identifier },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setIdentifying(true),
        onFinish: () => setIdentifying(false),
        onError: (identifyErrors) => setErrors(firstErrorPerField(identifyErrors)),
        onSuccess: (page) => goToStep(identifiedVerifyStep(page))
      }
    );
  };

  const submitPassword: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setErrors({});

    router.post(
      `/auth/resource/${resourceId}/verify/password`,
      {
        identifier,
        password,
        ...(redirectPage ? { redirectPage } : {}),
        ...(returnTo ? { returnTo } : {}),
        ...(embedded ? { embedded: true } : {}),
        ...(embedded && twoFactor === 'inline' ? { twoFactor } : {})
      },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setProcessing(true),
        onError: (loginErrors) => setErrors(firstErrorPerField(loginErrors)),
        // A failed attempt leaves the field populated with a password already known to
        // be wrong; clearing it keeps the next attempt from silently resubmitting it.
        onFinish: () => {
          setProcessing(false);
          setPassword('');
        }
      }
    );
  };

  /**
   * The passwordless step can offer both emailed options at once, and each has its own
   * sent flag, so every request clears the other's confirmation rather than leaving a
   * banner describing the option the visitor just moved on from.
   */
  const clearRequestFeedback = () => {
    setErrors({});
    setMagicLinkSent(false);
    setSetPasswordSent(false);
  };

  const submitMagicLink = () => {
    clearRequestFeedback();

    router.post(
      `/auth/resource/${resourceId}/magic-link`,
      {
        identifier,
        ...(redirectPage ? { redirectPage } : {}),
        ...(returnTo === 'current' && currentPageId ? { returnTo, currentPageId } : {}),
        ...inlineTwoFactorFields(twoFactor, currentPageId)
      },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setMagicLinkProcessing(true),
        onFinish: () => setMagicLinkProcessing(false),
        onError: (magicLinkErrors) => setErrors(firstErrorPerField(magicLinkErrors)),
        onSuccess: () => setMagicLinkSent(true)
      }
    );
  };

  /**
   * The account has no password to prompt for, so the same endpoint that mails a
   * reset link doubles as the way to set the first one — the emailed page reads as
   * "set" rather than "reset" because the link records that the account had none.
   */
  const submitSetPassword = () => {
    clearRequestFeedback();

    router.post(
      `/auth/resource/${resourceId}/forgot-password`,
      { identifier, ...inlineTwoFactorFields(twoFactor, currentPageId) },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setSetPasswordProcessing(true),
        onFinish: () => setSetPasswordProcessing(false),
        onError: (setPasswordErrors) => setErrors(firstErrorPerField(setPasswordErrors)),
        onSuccess: () => setSetPasswordSent(true)
      }
    );
  };

  const changeEmail = () => {
    setPassword('');
    goToStep('email');
  };

  const banner = magicLinkSent
    ? MAGIC_LINK_SENT_MESSAGE
    : setPasswordSent
      ? SET_PASSWORD_SENT_MESSAGE
      : statusConsumed
        ? null
        : statusMessage;

  const lockedIdentifierInput = (
    <FormTextInput
      type="email"
      label={identifierLabel}
      placeholder={identifierPlaceholder}
      value={identifier}
      onChange={setIdentifier}
      disabled
    />
  );

  const optionDivider = (
    <div className="mt-2 flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-medium text-muted-foreground">OR</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );

  const changeEmailControl = (
    <div className="mt-2 flex justify-center">
      <button
        type="button"
        onClick={changeEmail}
        className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline hover:underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        Use a different email
      </button>
    </div>
  );

  if (step === 'forgot') {
    return (
      <ForgotPasswordForm
        resourceId={resourceId}
        initialIdentifier={identifier}
        className={className}
        buttonClassName={buttonClassName}
        onBack={() => goToStep('verify')}
        twoFactor={twoFactor}
        returnTo={returnTo}
        currentPageId={currentPageId}
        router={router}
      />
    );
  }

  if (step === 'email') {
    return (
      <div className={cn('grid gap-2', className)}>
        {banner ? <AuthStatusMessage className="mb-2">{banner}</AuthStatusMessage> : null}

        <form onSubmit={submitIdentifier} className="grid gap-2">
          <FormTextInput
            type="email"
            label={identifierLabel}
            placeholder={identifierPlaceholder}
            autoComplete="email"
            value={identifier}
            onChange={setIdentifier}
            error={errors.identifier}
          />

          <Button
            type="submit"
            className={cn('mt-2 w-full', buttonClassName)}
            disabled={identifying || !identifier}
          >
            {continueLabel}
          </Button>
        </form>
      </div>
    );
  }

  /**
   * An authenticatable record is not required to carry a password, and one without
   * it can never satisfy a password prompt. Offer every way in the resource actually
   * has rather than a field that is guaranteed to fail — setting a password leads
   * first, directly under the prompt explaining the account has none, with the sign-in
   * link past the divider as the alternative that skips passwords entirely.
   */
  if (step === 'verify-passwordless') {
    return (
      <div className={cn('grid gap-2', className)}>
        {banner ? <AuthStatusMessage className="mb-2">{banner}</AuthStatusMessage> : null}

        {lockedIdentifierInput}

        {passwordResetEnabled ? (
          <>
            <p className="mt-2 text-sm text-muted-foreground">{SET_PASSWORD_PROMPT}</p>

            <Button
              type="button"
              className={cn('mt-2 w-full', buttonClassName)}
              disabled={setPasswordProcessing}
              onClick={submitSetPassword}
            >
              <Lock className="size-4" />
              {setPasswordLabel}
            </Button>

            {errors.identifier ? (
              <p className="mt-2 text-sm text-destructive">{errors.identifier}</p>
            ) : null}
          </>
        ) : null}

        {magicLinkEnabled && passwordResetEnabled ? optionDivider : null}

        {magicLinkEnabled ? (
          <>
            <p className="mt-2 text-sm text-muted-foreground">{MAGIC_LINK_PROMPT}</p>

            <Button
              type="button"
              variant={passwordResetEnabled ? 'outline' : 'default'}
              className={cn('mt-2 w-full', buttonClassName)}
              disabled={magicLinkProcessing}
              onClick={submitMagicLink}
            >
              <Mail className="size-4" />
              {magicLinkLabel}
            </Button>

            {errors.magic_link ? (
              <p className="mt-2 text-sm text-destructive">{errors.magic_link}</p>
            ) : null}
          </>
        ) : null}

        {!magicLinkEnabled && !passwordResetEnabled ? (
          <p className="mt-2 text-sm text-muted-foreground">{NO_SIGN_IN_METHOD_MESSAGE}</p>
        ) : null}

        {changeEmailControl}
      </div>
    );
  }

  return (
    <div className={cn('grid gap-2', className)}>
      {banner ? <AuthStatusMessage className="mb-2">{banner}</AuthStatusMessage> : null}

      {lockedIdentifierInput}

      <form onSubmit={submitPassword} className="grid gap-2">
        <FormPasswordInput
          label={passwordLabel}
          placeholder={passwordPlaceholder}
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          error={errors.password ?? errors.identifier}
        />

        {passwordResetEnabled ? (
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={() => goToStep('forgot')}
              className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline hover:underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Forgot password?
            </button>
          </div>
        ) : null}

        <Button
          type="submit"
          className={cn('w-full', buttonClassName)}
          disabled={processing || !password}
        >
          <Lock className="size-4" />
          {submitLabel}
        </Button>
      </form>

      {magicLinkEnabled ? (
        <>
          {optionDivider}
          <Button
            type="button"
            variant="outline"
            className={cn('mt-2 w-full', buttonClassName)}
            disabled={magicLinkProcessing}
            onClick={submitMagicLink}
          >
            <Mail className="size-4" />
            {magicLinkLabel}
          </Button>

          {errors.magic_link ? (
            <p className="mt-2 text-sm text-destructive">{errors.magic_link}</p>
          ) : null}
        </>
      ) : null}

      {changeEmailControl}
    </div>
  );
}
